import { useQueries } from '@tanstack/react-query';
import { getAvailableMeasures } from '../../api/work-periods-client/work-periods-client';
import { type MeasureMetadata, ModuleType } from '../../api/work-periods-client/work-periods-client.type';
import { useGlobalStore } from '../../store/global-store/global-store';

/**
 * Custom hook to fetch available measures for calculations based on module type
 *
 * @returns {object} - An object containing the fetched measures data and loading state
 * @returns {MeasureMetadata[]} returns.financialMeasures - Array of financial measure metadata
 * @returns {MeasureMetadata[]} returns.processAnalysisMeasures - Array of process analysis measure metadata
 * @returns {MeasureMetadata[]} returns.dashboardMeasures - Array of dashboard measure metadata
 * @returns {MeasureMetadata[]} returns.workPeriodMeasures - Array of work period measure metadata
 * @returns {boolean} returns.isLoading - Loading state of the queries
 */

const useCalculationsAvailableMeasures = (): {
  financialMeasures: MeasureMetadata[];
  processAnalysisMeasures: MeasureMetadata[];
  dashboardMeasures: MeasureMetadata[];
  workPeriodMeasures: MeasureMetadata[];
  isLoading: boolean;
} => {
  const organizationId = useGlobalStore((state) => state.organization?.id);

  // Define all module types we want to query
  const moduleTypes = [ModuleType.Financial, ModuleType.ProcessAnalysis, ModuleType.Dashboard, ModuleType.WorkPeriod];

  // Use useQueries to fetch all measures in parallel
  const queries = useQueries({
    queries: moduleTypes.map((moduleType) => ({
      queryKey: ['available-measures', moduleType, organizationId],
      queryFn: () =>
        getAvailableMeasures({
          scope: [moduleType],
          ...(moduleType === ModuleType.WorkPeriod && { work_period_id: '00000000-0000-0000-0000-000000000000' }),
          organization_id: organizationId,
        }),
      enabled: !!organizationId,
      staleTime: Infinity,
    })),
  });

  // Extract measures for each module type
  const getMeasuresForModule = (moduleType: ModuleType, queryIndex: number): MeasureMetadata[] => {
    const queryData = queries[queryIndex]?.data;
    return queryData?.[moduleType]?.measures ?? [];
  };

  // Check if any query is still loading
  const isLoading = queries.some((query) => query.isLoading);

  return {
    financialMeasures: getMeasuresForModule(ModuleType.Financial, 0),
    processAnalysisMeasures: getMeasuresForModule(ModuleType.ProcessAnalysis, 1),
    dashboardMeasures: getMeasuresForModule(ModuleType.Dashboard, 2),
    workPeriodMeasures: getMeasuresForModule(ModuleType.WorkPeriod, 3),
    isLoading,
  };
};

export { useCalculationsAvailableMeasures };
